import React, { useState, useEffect, useRef } from "react";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { Button, Form, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PasswordChecklist from "react-password-checklist";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import axios from "axios";
import urls from "../../Utils/urls";
import { commonToast } from "../../Utils";
import { FloatLabelInput, LoadingButton } from "../../Utils/CustomComponent";
import { Background } from "../Login/LoginComp";

const ForgotPassword = ({ closeModal }) => {
  const {
    handleSubmit,
    formState,
    setError,
    register,
    clearErrors,
    watch,
    setValue,
    getValues,
    trigger,
  } = useForm({
    defaultValues: {},
  });

  const [emailValidated, setEmailValidated] = useState(false);
  const [keyValidated, setKeyValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");

  // Add a state variable to manage whether passwords match or not
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [otpEntered, setOtpEntered] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const navigate = useNavigate();

  const keyVal = watch("key");
  const password = watch("password");
  const confirmPassword = watch("confirmPassword");
  const emailVal = watch("email");

  useEffect(() => {
    // Delay check to allow autofill
    const timeout = setTimeout(() => {
      const email = getValues("email");
      if (email && email.trim() !== "") {
        setValue("email", email.trim(), { shouldValidate: true });
        trigger("email");
      }
    }, 1000); // Allow browser autofill/render time

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    setValue("key", otpEntered);
    clearErrors("key");
  }, [otpEntered]);

  useEffect(() => {
    if (password === confirmPassword) {
      clearErrors("confirmPassword");
      setPasswordsMatch(true);
    } else {
      setPasswordsMatch(false);
    }
  }, [password, confirmPassword]);

  const onSubmit = async (data) => {
    if (!emailValidated) {
      setLoading(true);
      try {
        const response = await axios.post(urls.forgotPassword.postEmail, {
          email: data.email,
        });

        if (response.data === "OK") {
          setEmailValidated(true);
        } else {
          setError("email", {
            type: "manual",
            message: "Email is not valid",
          });
        }
      } catch (error) {
        console.error("API error:", error);
        setError("email", {
          type: "manual",
          message: error?.response?.data?.message ?? "Email is not valid",
        });
      } finally {
        setLoading(false);
      }
    } else if (data.key && !keyValidated) {
      setLoading(true);
      try {
        const response = await axios.post(urls.forgotPassword.postTempKey, {
          email: data.email,
          temp_key: data.key,
        });
        if (response.data.status === "200") {
          setUserId(response.data.user_id);
          setKeyValidated(true);
        } else {
          setError("key", {
            type: "manual",
            message: "Key is not valid",
          });
          setKeyValidated(false);
        }
      } catch (error) {
        console.error("API error:", error);
        setError("key", {
          type: "manual",
          message: "Key is not valid",
        });
        setKeyValidated(false);
      } finally {
        setLoading(false);
      }
    } else if (keyValidated) {
      setLoading(true);
      if (!passwordsMatch) {
        setError("confirmPassword", {
          type: "manual",
          message: "Password and Confirm Password must match",
        });
      } else {
        clearErrors("confirmPassword");
        const id = toast.loading("Resetting Password");
        try {
          const response = await axios.post(urls.profile.postChangePassword, {
            user_id: userId,
            password: data.confirmPassword,
            type: "U",
            email_address: "",
          });
          if (response.data.status === "200") {
            toast.update(id, {
              render: "Password Changed Successfully",
              type: "success",
              isLoading: false,
              ...commonToast,
            });
          } else {
            setError("password", {
              type: "manual",
              message: "Password reset failed",
            });
            toast.update(id, {
              render: "Password reset failed",
              type: "error",
              isLoading: false,
              ...commonToast,
            });
          }
        } catch (error) {
          console.error("API error:", error);
        } finally {
          setLoading(false);
          navigate("/login");
          // closeModal();
        }
      }
    }
  };

  async function resendOtp() {
    try {
      const response = await axios.post(urls.forgotPassword.postEmail, {
        email: emailVal,
      });
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <Background>
      <div className="w-75">
        <h3 className="pb-5 mb-3">Reset Password</h3>
        <div>
          <form>
            <FloatLabelInput
              register={{
                ...register("email", {
                  required: true,
                  pattern:
                    // eslint-disable-next-line no-useless-escape
                    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                }),
              }}
              label={"Enter your email"}
              value={emailVal}
              disabled={emailValidated}
              error={
                formState.errors.email
                  ? formState.errors.email?.message ?? "Email is Required!"
                  : ""
              }
              type="email"
            />

            {emailValidated ? (
              <div>
                <OTPInputComponent
                  setOtpEntered={setOtpEntered}
                  keyValidated={keyValidated}
                  onClick={resendOtp}
                />
                {formState.errors.key && (
                  <p style={{ color: "red" }}>{formState.errors.key.message}</p>
                )}
                {!keyValidated && (
                  <p>Please check your email to find the OTP.</p>
                )}
              </div>
            ) : null}

            {keyValidated ? (
              <div>
                <div
                  className="form-outline mb-1 mt-5"
                  style={{ position: "relative" }}
                >
                  <FloatLabelInput
                    register={{
                      ...register("password", {
                        required: "Password is required",
                        // pattern: {
                        //   value:
                        //     /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&\-])[A-Za-z\d@$!%*?&\-]{8,30}$/,
                        //   message:
                        //     "Password must be 8-30 characters long, include at least one uppercase letter, one lowercase letter, one number, and one special character.",
                        // },
                      }),
                    }}
                    label={"Password"}
                    value={password}
                    error={
                      formState.errors.password
                        ? formState.errors.password?.message
                        : ""
                    }
                    type={showPassword ? "text" : "password"}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "16px",
                      right: "2rem",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowPassword((pass) => !pass)}
                  >
                    {showPassword ? <Eye size={20} /> : <EyeSlash size={20} />}
                  </div>
                </div>
                <div
                  className="form-outline mt-5"
                  style={{ position: "relative" }}
                >
                  <FloatLabelInput
                    register={{
                      ...register("confirmPassword", { required: true }),
                    }}
                    label={"Confirm Password"}
                    value={confirmPassword}
                    error={
                      formState.errors.confirmPassword
                        ? formState.errors.confirmPassword.message
                        : ""
                    }
                    type={showPassword1 ? "text" : "password"}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "16px",
                      right: "2rem",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowPassword1((pass) => !pass)}
                  >
                    {showPassword1 ? <Eye size={20} /> : <EyeSlash size={20} />}
                  </div>
                  {/* {formState.errors.confirmPassword && (
                    <p style={{ color: "red" }}>
                      {formState.errors.confirmPassword.message}
                    </p>
                  )} */}
                </div>
                <PasswordChecklist
                  rules={[
                    "minLength",
                    "specialChar",
                    "number",
                    "capitalAndLowercase",
                    "match",
                    "noSequence",
                  ]}
                  minLength={8}
                  maxLength={30}
                  value={password || ""}
                  valueAgain={confirmPassword}
                  onChange={(isValid) => setIsPasswordValid(isValid)}
                  messages={{
                    minLength: "Contain 8 to 30 characters",
                    capitalAndLowercase:
                      "Contain both lower and uppercase letters",
                    number: "Contain 1 number",
                    specialChar: "Contain 1 special character - !@#$%^&*()_+",
                    noSequence:
                      "Not contain letter or number sequences like 'abc', '123', '4444', 'qwerty'",
                  }}
                  customRules={{
                    noSequence: (password) => {
                      // Disallow common sequences like 'abc', '123', '4444', 'qwerty'
                      const disallowedPatterns = [
                        /(abc|123|qwerty)/i,
                        /(.)\1\1\1/, // Repeating characters like "4444"
                      ];
                      return !disallowedPatterns.some((pattern) =>
                        pattern.test(password)
                      );
                    },
                  }}
                />
              </div>
            ) : null}

            <div className="d-flex justify-content-end">
              {keyValidated ? (
                <LoadingButton
                  loading={loading}
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                  className="text-white mt-5"
                  // title={keyValidated ? "Submit" : "Next"}
                  title={"Submit"}
                  // disabled={
                  //   loading ||
                  //   (!passwordsMatch && !formState.errors.confirmPassword)
                  // }
                  disabled={loading || !isPasswordValid}
                />
              ) : (
                <LoadingButton
                  loading={loading}
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                  className="text-white mt-5"
                  // title={keyValidated ? "Submit" : "Next"}
                  title={"Next"}
                  disabled={
                    loading ||
                    (!passwordsMatch && !formState.errors.confirmPassword)
                  }
                />
              )}
            </div>
          </form>
          <style>
            {`.span-color span {
          color: black;
         }
        `}
          </style>
        </div>
      </div>
    </Background>
  );
};

export default ForgotPassword;

const OTPInputComponent = ({ setOtpEntered, keyValidated, onClick }) => {
  const length = 6; // Length of OTP
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const [label, setLabel] = useState("");
  const inputsRef = useRef(new Array(length).fill(React.createRef()));
  const [timeLeft, setTimeLeft] = useState(120); // Timer for resend button

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false; // If not a number, don't process

    // Update OTP array
    const newOtp = [
      ...otp.map((d, idx) => (idx === index ? element.value : d)),
    ];
    setOtp([...newOtp]);
    setOtpEntered(newOtp.join(""));

    // Focus next input on value entry, previous if empty (backspace/delete)
    if (element.value !== "") {
      // Move to next input if available
      if (element.nextSibling) {
        element.nextSibling.focus();
      }
    } else if (element.previousSibling) {
      // Move to previous input on backspace/delete if available
      element.previousSibling.focus();
    }
  };

  useEffect(() => {
    // Automatically focus the first input on component mount
    inputsRef.current[0].focus();
  }, []);

  useEffect(() => {
    // Timer logic
    let interval;
    if (timeLeft === 0) {
    } else if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((timeLeft) => timeLeft - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const handleResendClick = () => {
    // Resend OTP logic here
    if (timeLeft <= 0) {
      // Call handleInitialLogin to resend OTP
      onClick();
      setTimeLeft(120); // Reset the timer
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text/plain")
      .slice(0, length)
      .split("");
    if (pastedData?.length === length) {
      setOtp([...pastedData]);
      setOtpEntered(pastedData.join(""));
      inputsRef.current[length - 1].focus();
    }
  };

  return (
    <>
      <label>Enter the OTP:</label>
      <InputGroup className="gap-5 mt-1">
        {Array.from({ length }, (_, index) => (
          <Form.Control
            key={index}
            type="text"
            maxLength="1"
            style={{
              width: "3rem",
              textAlign: "center",
              borderRadius: "0.25rem",
            }}
            value={otp[index]}
            onChange={(e) => handleChange(e.target, index)}
            onKeyDown={(e) => {
              // When the user presses backspace and the input is empty, focus the previous input
              if (e.key === "Backspace" && !otp[index]) {
                const prevInput = inputsRef.current[index - 1];
                if (prevInput) {
                  prevInput.focus();
                }
              }
            }}
            onPaste={handlePaste}
            ref={(ref) => (inputsRef.current[index] = ref)}
            className="inputStyle form-control input-lg p-5"
            disabled={keyValidated}
          />
        ))}
      </InputGroup>
      {!keyValidated ? (
        <Button
          variant="link"
          className="w-100 text-decoration-underline text-end p-0"
          onClick={handleResendClick}
          disabled={timeLeft > 0}
        >
          {timeLeft > 0 ? `Resend OTP in ${timeLeft}s` : "Resend OTP"}
        </Button>
      ) : null}
    </>
  );
};
